import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Signup = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await signup(email, password, name);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-concrete-white flex" data-testid="signup-page">
      {/* Form Side */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <span className="font-anton text-3xl tracking-tight">VINTAGE</span>
            <span className="font-bebas text-3xl text-chicago-red">KICKS</span>
          </div>

          <div className="text-center mb-8">
            <h1 className="font-anton text-4xl tracking-tighter uppercase">JOIN THE COLLECTORS</h1>
            <p className="text-cement-grey mt-2">Create your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="font-bebas tracking-wider">NAME</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your Name"
                className="mt-1 rounded-sm"
                data-testid="signup-name"
              />
            </div>
            <div>
              <Label htmlFor="email" className="font-bebas tracking-wider">EMAIL</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="mt-1 rounded-sm"
                data-testid="signup-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-bebas tracking-wider">PASSWORD</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 rounded-sm"
                data-testid="signup-password"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="font-bebas tracking-wider">CONFIRM PASSWORD</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 rounded-sm"
                data-testid="signup-confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-chicago-red hover:bg-chicago-red/90 text-white uppercase tracking-widest text-sm font-bold py-6 rounded-sm btn-primary"
              data-testid="signup-submit"
            >
              {loading ? (
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <p className="text-center text-cement-grey mt-8">
            Already have an account?{' '}
            <Link to="/login" className="text-chicago-red font-bold hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Image Side */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1765036464882-e36092ba4bb4?w=1200&q=80"
          alt="Basketball"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-deep-obsidian/60" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="font-anton text-4xl text-white tracking-tight">VINTAGE</span>
              <span className="font-bebas text-4xl text-chicago-red">KICKS</span>
            </div>
            <p className="text-cement-grey text-lg max-w-md">
              Join thousands of collectors who trust us for authentic vintage basketball shoes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
